import { useEffect, useLayoutEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowRight, Check, FileText, Globe, Mail, Plus, Trash2 } from "lucide-react";
import { parseBlockContent, type BlockContent } from "../../../lib/blockContentSchemas";
import type { EditorBlock, EditorPage } from "../editor.types";
import { MenuSegmented, MenuSwatches, MenuTextInput, menuSurfaceClass } from "./Menu";
import { BUTTON_COLOURS, THEME_BUTTON_COLOURS, type ButtonColour } from "./buttonColours";
import type { SiteThemeId } from "../../themes";
import {
  parseLink,
  pendingButtonSettings,
  sectionAnchor,
  withSecondButton,
  type SectionTarget,
} from "./buttonSettings";
import "./ButtonBlock.css";

type Button = BlockContent<"button">["buttons"][number];
type ButtonStyle = Button["style"];

const STYLES: [ButtonStyle, string][] = [
  ["solid", "Solid"],
  ["outline", "Outline"],
  ["link", "Link"],
];


/** Only web, email and phone links leave the site. */
const safeUrl = (url: string | undefined) =>
  url && /^(https?:\/\/|mailto:|tel:)/i.test(url) ? url : undefined;

/** How a button's link reads in its settings. */
const linkText = (button: Button, pages: EditorPage[], sections: SectionTarget[]) =>
  button.section
    ? (sections.find((section) => section.id === button.section)?.label ?? "")
    : button.page
      ? (pages.find((page) => page.source_page_id === button.page)?.title ?? "")
      : (button.url ?? "").replace(/^mailto:/i, "").replace(/^https?:\/\//i, "");

/** Glides down to a section on the page (or jumps, for reduced motion). */
const scrollToSection = (id: string) => {
  const target = document.getElementById(sectionAnchor(id));
  if (!target) return false;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  history.replaceState(history.state, "", `#${sectionAnchor(id)}`);
  return true;
};

type ButtonBlockProps = {
  block: EditorBlock;
  pages: EditorPage[];
  pageHref?: (page: EditorPage) => string;
  /** Opens one of the site's pages in place (the live site and Preview). */
  onOpenPage?: (page: EditorPage) => void;
  /** In the editor: the page's sections, which a button can link to. */
  sections?: SectionTarget[];
  /** In the editor: saves the block's content (buttons are edited, not followed). */
  onUpdate?: (content: BlockContent<"button">) => void;
};

/** One or two call-to-action buttons: Solid, Outline or Link. */
export function ButtonBlock({ block, pages, pageHref, onOpenPage, sections = [], onUpdate }: ButtonBlockProps): ReactNode {
  const content = parseBlockContent("button", block.content);
  const [chosen, setChosen] = useState<{ index: number; fresh: boolean } | null>(null);
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  // A new block, or a just-added second button, opens its settings.
  const pending = onUpdate ? pendingButtonSettings.get(block.source_block_id) : undefined;
  const editing = chosen ?? (pending !== undefined && content.buttons[pending] ? { index: pending, fresh: true } : null);
  // Closing a pending one changes no state of ours, so count closes too.
  const [, closed] = useReducer((count: number) => count + 1, 0);
  const setEditing = (next: { index: number; fresh: boolean } | null) => {
    pendingButtonSettings.delete(block.source_block_id);
    setChosen(next);
    closed();
  };
  const update = (index: number, patch: Partial<Button>) =>
    onUpdate?.({
      ...content,
      buttons: content.buttons.map((button, i) => (i === index ? { ...button, ...patch } : button)),
    });
  if (!onUpdate)
    return (
      <div className="site-buttons" data-align={content.align}>
        {content.buttons.map((button, i) => {
          const page = button.page ? pages.find((p) => p.source_page_id === button.page) : undefined;
          const section = button.section;
          const href = section ? `#${sectionAnchor(section)}` : page ? pageHref?.(page) : safeUrl(button.url);
          const away = Boolean(!section && !page && href && /^https?:/i.test(href));
          return (
            <a
              key={i}
              className="site-button"
              data-style={button.style}
              data-tone={button.tone}
              href={href}
              {...(away ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={(event) => {
                if (section && scrollToSection(section)) event.preventDefault();
                // A page of the site opens in place, like its menu's links
                // (a new tab with a modifier key, as usual).
                else if (page && onOpenPage && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
                  event.preventDefault();
                  onOpenPage(page);
                }
              }}
            >
              {button.label}
              {button.style === "link" && <ArrowRight size={16} aria-hidden="true" />}
            </a>
          );
        })}
      </div>
    );
  return (
    <div ref={setRoot} className="site-buttons" data-align={content.align}>
      {content.buttons.map((button, i) => (
        <button
          key={i}
          type="button"
          data-index={i}
          className="site-button"
          data-style={button.style}
          data-tone={button.tone}
          data-editing={editing?.index === i}
          aria-label={`${button.label}: edit button`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            setEditing({ index: i, fresh: false });
          }}
        >
          {button.label || "Button"}
          {button.style === "link" && <ArrowRight size={16} aria-hidden="true" />}
        </button>
      ))}
      {content.buttons.length < 2 && (
        // Beside the button without taking room, so a lone button sits
        // where it will on the live site (centred, say).
        <span className="site-button-add-slot">
          <button
            type="button"
            className="site-button-add"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              onUpdate(withSecondButton(content));
              setEditing({ index: 1, fresh: true });
            }}
          >
            <Plus size={14} aria-hidden="true" />
            Add a button
          </button>
        </span>
      )}
      {editing && content.buttons[editing.index] && (
        <ButtonSettings
          key={editing.index}
          root={root}
          index={editing.index}
          button={content.buttons[editing.index]}
          pages={pages}
          sections={sections}
          fresh={editing.fresh}
          onChange={(patch) => update(editing.index, patch)}
          onRemove={
            content.buttons.length > 1
              ? () => {
                  onUpdate({ ...content, buttons: content.buttons.filter((_, i) => i !== editing.index) });
                  setEditing(null);
                }
              : undefined
          }
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/**
 * A button's settings, beside it: its label, where it links (a section on
 * this page, one of your pages, a web address or an email address) and its
 * style. On phones it's a sheet along the bottom of the screen.
 */
/** The site theme around the block, for drawing its colours in a menu. */
const themeOf = (root: HTMLElement | null) => {
  const themed = root?.closest<HTMLElement>("[data-site-theme]");
  return {
    "data-site-theme": themed?.dataset.siteTheme,
    "data-site-accent": themed?.dataset.siteAccent,
  };
};

function ButtonSettings({
  root,
  index,
  button,
  pages,
  sections,
  fresh,
  onChange,
  onRemove,
  onClose,
}: {
  /** The block's buttons, to find the one being edited beside. */
  root: HTMLElement | null;
  index: number;
  button: Button;
  pages: EditorPage[];
  sections: SectionTarget[];
  fresh: boolean;
  onChange: (patch: Partial<Button>) => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [link, setLink] = useState(() => linkText(button, pages, sections));
  const [choosing, setChoosing] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const narrow = window.matchMedia?.("(max-width: 519px)").matches ?? false;
  // Beside the button (below it, or above when there's no room below); on
  // phones a sheet along the bottom, kept above the keyboard.
  useLayoutEffect(() => {
    const element = panel.current;
    const anchor = root?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    if (!element || !anchor) return;
    const viewport = window.visualViewport;
    const place = () => {
      if (narrow) {
        const keyboard = viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0;
        element.style.bottom = `calc(${keyboard + 12}px + env(safe-area-inset-bottom))`;
        return;
      }
      const box = anchor.getBoundingClientRect();
      const height = element.offsetHeight;
      const below = box.bottom + 8;
      const top = below + height > window.innerHeight - 12 ? Math.max(12, box.top - 8 - height) : below;
      element.style.top = `${top}px`;
      element.style.left = `${Math.max(12, Math.min(box.left, window.innerWidth - element.offsetWidth - 12))}px`;
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    viewport?.addEventListener("resize", place);
    viewport?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      viewport?.removeEventListener("resize", place);
      viewport?.removeEventListener("scroll", place);
    };
  }, [root, index, narrow, choosing]);
  // Closes on Escape, or a press anywhere else.
  useEffect(() => {
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      const anchor = root?.querySelector(`[data-index="${index}"]`);
      if (panel.current?.contains(target) || anchor?.contains(target)) return;
      onClose();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", away, true);
    window.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away, true);
      window.removeEventListener("keydown", key);
    };
  }, [root, index, onClose]);
  const query = link.trim().toLowerCase();
  const matches = pages
    .filter((page) => !query || page.title.toLowerCase().includes(query))
    .slice(0, 5);
  const sectionMatches = sections
    .filter((section) => !query || section.label.toLowerCase().includes(query))
    .slice(0, 4);
  /** Settles what was typed: a page, a web or email address, or nothing yet. */
  const commit = () => {
    setChoosing(false);
    const value = link.trim();
    if (!value) {
      onChange({ page: undefined, section: undefined, url: undefined });
      setLinkError(null);
      return;
    }
    const section = sections.find((s) => s.label.toLowerCase() === value.toLowerCase());
    if (section) return chooseSection(section);
    const page = pages.find((p) => p.title.toLowerCase() === value.toLowerCase());
    if (page) return choosePage(page);
    const url = parseLink(value);
    if (url) {
      onChange({ page: undefined, section: undefined, url });
      setLinkError(null);
    } else setLinkError("Choose a section or page, or enter a web or email address.");
  };
  const choosePage = (page: EditorPage) => {
    setLink(page.title);
    setChoosing(false);
    setLinkError(null);
    onChange({ page: page.source_page_id, section: undefined, url: undefined });
  };
  const chooseSection = (section: SectionTarget) => {
    setLink(section.label);
    setChoosing(false);
    setLinkError(null);
    onChange({ section: section.id, page: undefined, url: undefined });
  };
  const kind = button.section ? ArrowDown : button.page ? FileText : button.url?.startsWith("mailto:") ? Mail : Globe;
  const LinkIcon = kind;
  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-label="Button settings"
      className={`${menuSurfaceClass} fixed z-70 grid gap-2.5 p-3 ${narrow ? "inset-x-3 mx-auto max-w-120" : "w-72"}`}
      data-sheet={narrow || undefined}
    >
      <label className="grid gap-1 text-xs text-hazumu-muted">
        Label
        <MenuTextInput
          autoFocus={fresh && !narrow}
          value={button.label}
          maxLength={80}
          placeholder="Get in touch"
          onChange={(event) => onChange({ label: event.target.value })}
          onKeyDown={(event) => {
            if (event.key === "Enter") onClose();
          }}
        />
      </label>
      <div className="grid gap-1 text-xs text-hazumu-muted">
        <label htmlFor="button-link">Links to</label>
        <div className="flex items-center gap-2 rounded-lg border border-hazumu-border px-2.5 focus-within:border-hazumu-clay">
          <LinkIcon size={14} className="shrink-0 text-hazumu-muted" aria-hidden="true" />
          <input
            id="button-link"
            className="min-w-0 flex-1 bg-transparent py-1.5 text-[13px] text-hazumu-ink outline-none placeholder:text-hazumu-muted group-data-[sheet=true]/menu:text-base"
            value={link}
            placeholder="Page, section, website or email"
            autoComplete="off"
            onFocus={() => setChoosing(true)}
            onChange={(event) => {
              setLink(event.target.value);
              setChoosing(true);
            }}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commit();
              }
            }}
          />
        </div>
        {choosing && sectionMatches.length > 0 && (
          <div className="grid" role="listbox" aria-label="On this page">
            <span className="px-2 pt-1 text-[11px] text-hazumu-muted">On this page</span>
            {sectionMatches.map((section) => (
              <button
                key={section.id}
                type="button"
                role="option"
                aria-selected={button.section === section.id}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-hazumu-ink hover:bg-hazumu-canvas"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseSection(section)}
              >
                <ArrowDown size={14} className="shrink-0 text-hazumu-muted" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{section.label}</span>
                {button.section === section.id && <Check size={14} aria-hidden="true" />}
              </button>
            ))}
          </div>
        )}
        {choosing && matches.length > 0 && (
          <div className="grid" role="listbox" aria-label="Your pages">
            {sectionMatches.length > 0 && <span className="px-2 pt-1 text-[11px] text-hazumu-muted">Pages</span>}
            {matches.map((page) => (
              <button
                key={page.id}
                type="button"
                role="option"
                aria-selected={button.page === page.source_page_id}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-hazumu-ink hover:bg-hazumu-canvas"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choosePage(page)}
              >
                <FileText size={14} className="shrink-0 text-hazumu-muted" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{page.title}</span>
                {button.page === page.source_page_id && <Check size={14} aria-hidden="true" />}
              </button>
            ))}
          </div>
        )}
        {linkError && (
          <span role="alert" className="text-hazumu-danger">
            {linkError}
          </span>
        )}
      </div>
      <div className="grid gap-1 text-xs text-hazumu-muted">
        Style
        <MenuSegmented
          fullWidth
          label="Button style"
          options={STYLES}
          value={button.style}
          onChange={(style) => onChange({ style })}
        />
      </div>
      <div className="-mx-2.5 grid gap-1 text-xs text-hazumu-muted">
        <span className="px-2.5">Colour</span>
        {/* Drawn in the site's theme (this panel sits outside the page). */}
        <MenuSwatches<ButtonColour>
          label="Button colour"
          {...themeOf(root)}
          options={THEME_BUTTON_COLOURS[(themeOf(root)["data-site-theme"] as SiteThemeId) ?? "warm"].map((id) => ({
            id,
            label: BUTTON_COLOURS[id],
            className: "button-swatch",
            tone: id,
          }))}
          value={button.tone ?? "accent"}
          onChange={(tone) => onChange({ tone: tone === "accent" ? undefined : tone })}
        />
      </div>
      {onRemove && (
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-hazumu-danger hover:bg-hazumu-canvas"
          onClick={onRemove}
        >
          <Trash2 size={14} aria-hidden="true" />
          Remove this button
        </button>
      )}
    </div>,
    document.body,
  );
}
